/**
 * @fileoverview Shared document render-data assembly — the single source of truth that turns a
 * DocumentConfig + a normalized merge context into the DocumentRenderData both the worker PDF
 * pipeline and the web canvas preview render.
 * @module document-render/resolveDocumentData
 *
 * Responsibilities: - Resolve {{merge-field}} tokens (union of every field both transformers knew)
 *                   - Resolve each text-bearing section (header, recipient, subject, salutation,
 *                     body, metadata, comments, contact, legal, footer, static content)
 *                   - Group violations and slice photos by the document config
 *                   - Decide which sections are enabled (band-driven when a DocumentLayout is
 *                     present, else config.sections — fixes the dual enable-flag seam)
 *                   - Assemble the final DocumentRenderData (sections + branding + advanced fields)
 * Dependencies:     - ./documentConfig, ./documentLayout (types), ./renderPrimitives (status color)
 *                   - none external; HTML sanitization is injected by the caller so this package
 *                     stays dependency-free (the worker passes @firesquirrel/core's sanitizer).
 * Architecture:     - Pure functions, no I/O. The worker (Mongo) and web preview (Dexie) each
 *                     normalize their own source into a MergeFieldContext + pre-mapped primitives,
 *                     then call assembleDocumentRenderData. This eliminates the two ~900-line
 *                     parallel transformers that had silently drifted (the DRY root cause behind
 *                     the band/section gating and theme/branding divergence bugs).
 * Notes:            - Source-specific extraction that genuinely differs between Mongo and Dexie
 *                     (violation/photo/signature shapes, advanced-field truthfulness handling) is
 *                     done by each adapter and passed in pre-resolved — only the shared, identical
 *                     logic lives here.
 */
import type {
  DocumentConfig,
  ResolvedHeader,
  ResolvedRecipientBlock,
  ResolvedSubjectLine,
  ResolvedSalutation,
  ResolvedBodyParagraph,
  ResolvedMetadataField,
  ResolvedViolationItem,
  ResolvedViolationGroup,
  ResolvedPhoto,
  ResolvedComments,
  ResolvedContactBlock,
  ResolvedSignatures,
  ResolvedLegalText,
  ResolvedFooter,
  ResolvedStaticContent,
  ResolvedFormResponseGroup,
  ResolvedAdvancedField,
  DocumentRenderData,
} from './documentConfig';
import type { DocumentLayout, Band, BandKind } from './documentLayout';
import { complianceStatusColor } from './renderPrimitives';
import { resolveAdvancedTokensInHtml } from './advancedTokens';
import { applyLayoutHeaderLogos, resolveDepartmentLogoUrl, resolveDepartmentSecondLogoUrl } from './logoResolver';

/**
 * @contract MergeFieldContext
 * Purpose: Normalized, source-agnostic inputs the merge-field and section resolvers read. Both the
 *          worker (Mongo documents) and the web preview (Dexie/sample documents) build this shape.
 * Fields:  entity/series/event/inspector/department — loosely-typed source records (Mongo or Dexie);
 *          reportNumber/generatedAt — identifiers stamped at render time; violations — only `.length`
 *          is read here (for compliance status), the full resolved items are passed separately.
 * Rules:   Resolvers must tolerate missing fields (deep optional chaining); never throw on absent data.
 * Sync:    local-first agnostic — this is a pure value object assembled by each caller from its source.
 */
export interface MergeFieldContext {
  entity: Record<string, any>;
  series: Record<string, any>;
  event?: Record<string, any>;
  inspector: Record<string, any>;
  department: Record<string, any>;
  reportNumber: string;
  generatedAt: Date;
  violations: ReadonlyArray<unknown>;
}

/** Default department brand colors (Fire red + navy) used when a tenant has no configured brand. */
export const DEFAULT_DOCUMENT_BRANDING: { primary: string; secondary: string } = {
  primary: '#C41E3A',
  secondary: '#1A365D',
};

// ---------------------------------------------------------------------------
// Internal formatting helpers
// ---------------------------------------------------------------------------

/** Format a date as MM/DD/YYYY; returns '' for invalid/unparseable input. */
function formatDateShort(date: Date | string): string {
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${m}/${day}/${d.getFullYear()}`;
}

/**
 * Format a date as MM/DD/YYYY for document fields; returns '' for invalid input. Exported so the
 * per-source adapters' signature/form-response resolvers format dates identically to this module.
 */
export function formatDocumentDate(date: Date | string): string {
  return formatDateShort(date);
}

/** Join a loose address object into "street, city, state, zip", skipping blanks. */
function formatDeptAddress(addr: Record<string, any> | undefined): string {
  if (!addr) return '';
  const parts = [addr.street, addr.city, addr.state, addr.zip].filter(Boolean);
  return parts.join(', ');
}

/**
 * Resolve the primary owner from the Phase 2A canonical `ownership.owners`
 * block, if present. Returns the owner flagged `isPrimary`, falling back to
 * the first owner when none is flagged (mirrors
 * web/packages/core-domain/src/forms/parcelOwnerContact.ts's precedence).
 */
function resolvePrimaryOwner(entity: Record<string, any>): { name?: string } | undefined {
  const owners = entity?.ownership?.owners;
  if (!Array.isArray(owners) || owners.length === 0) return undefined;
  return owners.find((o: any) => o?.isPrimary) ?? owners[0];
}

/** Join the canonical `ownership.mailingAddress` shape into "line1[, line2], city, state postalCode". */
function formatOwnershipMailingAddress(addr: Record<string, any> | undefined): string {
  if (!addr) return '';
  const parts = [addr.line1, addr.line2, addr.city, addr.state, addr.postalCode].filter(Boolean);
  return parts.join(', ');
}

// ---------------------------------------------------------------------------
// Merge-field resolvers (union of the worker + web field sets — neither side
// loses a field after consolidation)
// ---------------------------------------------------------------------------

const MERGE_FIELD_RESOLVERS: Record<string, (ctx: MergeFieldContext) => string> = {
  // Phase 2A: canonical ownership.owners (primary) is preferred over the legacy
  // parcelDetails.ownerName / contacts[0].name fallbacks it now sits ahead of.
  ownerName: (ctx) =>
    resolvePrimaryOwner(ctx.entity)?.name
    || ctx.entity.ownerName
    || ctx.entity.parcelDetails?.ownerName
    || ctx.entity.contacts?.[0]?.name
    || ctx.entity.name
    || '',
  inspectionAddress: (ctx) =>
    ctx.entity.entityAddress
    || ctx.entity.entityLocation?.address?.fullAddress
    || '',
  // Phase 2A: canonical ownership.mailingAddress is preferred over the legacy
  // parcelDetails.mailingAddress. The situs (inspection) address fallback was
  // REMOVED per Docs/duckdb-geo-import-phase0-audit/11-parcel-owner-migration-review.md
  // §7.2 — a document must never silently show the property's physical address
  // as if it were the owner's mailing address. When no owner mailing data
  // exists, these fields resolve to '' rather than an address that may be wrong.
  mailingAddress: (ctx) =>
    formatOwnershipMailingAddress(ctx.entity.ownership?.mailingAddress)
    || ctx.entity.parcelDetails?.mailingAddress?.full
    || '',
  mailingStreet: (ctx) =>
    ctx.entity.ownership?.mailingAddress?.line1
    || ctx.entity.parcelDetails?.mailingAddress?.street
    || ctx.entity.ownerAddress
    || '',
  mailingCity: (ctx) =>
    ctx.entity.ownership?.mailingAddress?.city
    || ctx.entity.parcelDetails?.mailingAddress?.city
    || '',
  mailingState: (ctx) =>
    ctx.entity.ownership?.mailingAddress?.state
    || ctx.entity.parcelDetails?.mailingAddress?.state
    || '',
  mailingZip: (ctx) =>
    ctx.entity.ownership?.mailingAddress?.postalCode
    || ctx.entity.parcelDetails?.mailingAddress?.zip
    || '',
  /** Returns "City, State" when both present; only city or state when one is missing. Avoids orphan comma. */
  mailingCityState: (ctx) => {
    const city = ctx.entity.ownership?.mailingAddress?.city || ctx.entity.parcelDetails?.mailingAddress?.city || '';
    const state = ctx.entity.ownership?.mailingAddress?.state || ctx.entity.parcelDetails?.mailingAddress?.state || '';
    if (city && state) return `${city}, ${state}`;
    return city || state;
  },
  parcelNo: (ctx) => ctx.entity.parcelNumber || ctx.entity.parcelDetails?.parcelNumber || '',
  comments: (ctx) => ctx.event?.externalNotes || ctx.series?.externalNotes || '',
  apn: (ctx) => ctx.entity.parcelNumber || '',
  contactNumber: (ctx) =>
    ctx.entity.primaryContact?.phone
    || ctx.entity.contacts?.[0]?.phoneNumbers?.[0]?.number
    || '',
  emailAddress: (ctx) =>
    ctx.entity.primaryContact?.email
    || ctx.entity.contacts?.[0]?.emails?.[0]?.email
    || '',
  inspectionNumber: (ctx) => ctx.reportNumber || ctx.series._id?.toString() || '',
  inspectionDate: (ctx) => {
    const d = ctx.event?.completedAt || ctx.event?.startedAt || ctx.series.createdAt;
    return d ? formatDateShort(d) : '';
  },
  inspectorName: (ctx) =>
    ctx.inspector.name?.trim()
    || ctx.event?.primaryInspectorName
    || '',
  inspectorTitle: (ctx) => ctx.inspector.title || '',
  inspectorBadge: (ctx) => ctx.inspector.badgeNumber || '',
  departmentName: (ctx) => ctx.department.name || '',
  departmentAddress: (ctx) =>
    ctx.department.address?.fullAddress
    || formatDeptAddress(ctx.department.address)
    || '',
  departmentPhone: (ctx) =>
    ctx.department.phone
    || ctx.department.contactPhone
    || ctx.department.contacts?.[0]?.phones?.[0]?.number
    || '',
  departmentFax: (ctx) => ctx.department.fax || '',
  departmentEmail: (ctx) =>
    ctx.department.email
    || ctx.department.contactEmail
    || ctx.department.contacts?.[0]?.emails?.[0]?.address
    || '',
  departmentWebsite: (ctx) => ctx.department.website || '',
  inspectionType: (ctx) =>
    ctx.event?.eventType?.name
    || ctx.series.seriesType?.name
    || ctx.series.caseType?.name
    || '',
  complianceStatus: (ctx) => ctx.violations.length === 0 ? 'Compliant' : 'Non-Compliant',
  violationCount: (ctx) => String(ctx.violations.length),
  reportNumber: (ctx) => ctx.reportNumber,
  date: (ctx) => formatDateShort(ctx.generatedAt),
  year: (ctx) => String(ctx.generatedAt.getFullYear()),
  entityName: (ctx) => ctx.entity.name || '',
  entityCategory: (ctx) => ctx.entity.entityCategory || '',
};

/**
 * Resolve a single merge field like `ownerName` against the context. Unknown fields resolve to ''.
 */
export function resolveMergeField(field: string, ctx: MergeFieldContext): string {
  const resolver = MERGE_FIELD_RESOLVERS[field];
  if (resolver) return resolver(ctx);
  return '';
}

/**
 * Resolve every `{{field}}` placeholder in a text string against the context.
 */
export function resolveAllMergeFields(text: string, ctx: MergeFieldContext): string {
  return text.replace(/\{\{(\w+)\}\}/g, (_match, field) => resolveMergeField(field, ctx));
}

// ---------------------------------------------------------------------------
// Section resolvers (text-bearing sections — identical logic for both callers)
// ---------------------------------------------------------------------------

/** Identity sanitizer used when a caller (e.g. the web preview) does not inject one. */
const identitySanitize = (html: string) => html;

function resolveHeader(config: DocumentConfig, ctx: MergeFieldContext): ResolvedHeader {
  const h = config.header || {};
  return {
    // Legacy (non-canvas) gating: only shown when the document-level "show logo" flag is set.
    // Canvas-driven templates override these via applyLayoutHeaderLogos in assembleDocumentRenderData
    // below, where the band slot's own `show` flag is the sole gate (see logoResolver.ts notes).
    logoUrl: h.showLogo ? resolveDepartmentLogoUrl(ctx.department.branding) : undefined,
    secondLogoUrl: h.showSecondLogo ? resolveDepartmentSecondLogoUrl(ctx.department.branding) : undefined,
    logoPosition: h.logoPosition || 'left',
    secondLogoPosition: h.secondLogoPosition || 'right',
    departmentName: h.showDepartmentName ? (ctx.department.name || '') : undefined,
    departmentAddress: h.showDepartmentAddress
      ? (ctx.department.address?.fullAddress || formatDeptAddress(ctx.department.address))
      : undefined,
    departmentPhone: h.showDepartmentPhone ? (ctx.department.phone || '') : undefined,
    departmentFax: h.showDepartmentFax ? (ctx.department.fax || '') : undefined,
    departmentWebsite: h.showDepartmentWebsite ? (ctx.department.website || '') : undefined,
    title: h.customTitle ? resolveAllMergeFields(h.customTitle, ctx) : undefined,
    subtitle: h.customSubtitle ? resolveAllMergeFields(h.customSubtitle, ctx) : undefined,
    titleStyle: h.titleStyle,
    subtitleStyle: h.subtitleStyle,
  };
}

function resolveRecipientBlock(config: DocumentConfig, ctx: MergeFieldContext): ResolvedRecipientBlock {
  const r = config.recipientBlock || { style: 'grid' as const };
  const fields: Array<{ label: string; value: string }> = [];

  if (r.showOwnerName) fields.push({ label: 'Owner/Tenant', value: resolveMergeField('ownerName', ctx) });
  if (r.showInspectionAddress) fields.push({ label: 'Inspection Address', value: resolveMergeField('inspectionAddress', ctx) });
  if (r.showMailingAddress) fields.push({ label: 'Mailing Address', value: resolveMergeField('mailingAddress', ctx) });
  if (r.showApn) fields.push({ label: 'APN', value: resolveMergeField('apn', ctx) });
  if (r.showContactNumber) fields.push({ label: 'Contact #', value: resolveMergeField('contactNumber', ctx) });
  if (r.showEmailAddress) fields.push({ label: 'Email', value: resolveMergeField('emailAddress', ctx) });
  if (r.showInspectionNumber) fields.push({ label: 'Inspection #', value: resolveMergeField('inspectionNumber', ctx) });

  return { style: r.style, fields };
}

function resolveSubjectLine(config: DocumentConfig, ctx: MergeFieldContext): ResolvedSubjectLine {
  const s = config.subjectLine || {};
  const prefix = s.prefix || 'RE:';
  const template = s.template || '{{inspectionType}} — {{inspectionAddress}}';
  return { text: `${prefix} ${resolveAllMergeFields(template, ctx)}` };
}

function resolveSalutation(config: DocumentConfig, ctx: MergeFieldContext): ResolvedSalutation {
  const s = config.salutation || {};
  const text = s.text || 'Dear Property Owner/Responsible Party,';
  return { text: resolveAllMergeFields(text, ctx) };
}

function resolveBodyText(
  config: DocumentConfig,
  ctx: MergeFieldContext,
  hasViolations: boolean,
  sanitizeHtml: (html: string) => string,
): ResolvedBodyParagraph[] {
  const b = config.bodyText || {};
  const paragraphs = b.paragraphs || [];
  const resolved: ResolvedBodyParagraph[] = [];

  for (const p of paragraphs) {
    const condition = p.condition || 'always';
    if (condition === 'always'
      || (condition === 'hasViolations' && hasViolations)
      || (condition === 'noViolations' && !hasViolations)
    ) {
      const merged = resolveAllMergeFields(p.content, ctx);
      resolved.push({
        id: p.id,
        // HTML paragraphs are rendered raw; sanitize after merge-field resolution so injected
        // values are also cleaned. The caller injects the sanitizer (identity for the preview).
        content: p.isHtml ? sanitizeHtml(merged) : merged,
        isHtml: p.isHtml,
      });
    }
  }

  return resolved;
}

function resolveMetadata(config: DocumentConfig, ctx: MergeFieldContext): ResolvedMetadataField[] {
  const m = config.metadata || {};
  const fields: ResolvedMetadataField[] = [];

  if (m.showComplianceStatus) {
    const complianceValue = resolveMergeField('complianceStatus', ctx);
    fields.push({
      key: 'complianceStatus',
      label: 'Compliance Status',
      value: complianceValue,
      // Color the compliance status to match the on-screen preview.
      color: complianceStatusColor(complianceValue),
    });
  }
  if (m.showInspectionType) {
    fields.push({ key: 'inspectionType', label: 'Inspection Type', value: resolveMergeField('inspectionType', ctx) });
  }
  if (m.showSlope) {
    fields.push({
      key: 'slope',
      label: 'Slope',
      value: ctx.series.wildlandData?.slope || ctx.entity.customFieldValues?.slope?.value || '',
    });
  }
  if (m.showInspectionDate) {
    fields.push({ key: 'inspectionDate', label: 'Inspection Date', value: resolveMergeField('inspectionDate', ctx) });
  }
  if (m.showInspector) {
    fields.push({ key: 'inspector', label: 'Inspector', value: resolveMergeField('inspectorName', ctx) });
  }
  if (m.showAcreageEligible) {
    fields.push({ key: 'acreageEligible', label: 'Acreage Eligible', value: ctx.series.wildlandData?.acreageEligible ?? '' });
  }
  if (m.showPhotosFlag) {
    fields.push({
      key: 'photosFlag',
      label: 'Photos',
      value: (ctx.series.events || []).some((e: any) => e.photos?.length > 0) ? 'Yes' : 'No',
    });
  }
  if (m.showCurbsideVacant) {
    fields.push({ key: 'curbsideVacant', label: 'Curbside / Vacant', value: ctx.series.wildlandData?.curbsideVacant ?? '' });
  }
  if (m.showInsuranceSolicited) {
    fields.push({ key: 'insuranceSolicited', label: 'Insurance Solicited', value: ctx.series.wildlandData?.insuranceSolicited ?? '' });
  }
  if (m.showComplaintConstruction) {
    fields.push({ key: 'complaintConstruction', label: 'Complaint / Construction', value: ctx.series.wildlandData?.complaintConstruction ?? '' });
  }

  return fields;
}

function resolveComments(config: DocumentConfig, ctx: MergeFieldContext): ResolvedComments {
  const c = config.comments || {};
  const event = ctx.event || {};
  return {
    externalNotes: c.showCommentsBox ? (event.externalNotes || ctx.series.externalNotes || '') : undefined,
    internalNotes: undefined, // Internal notes are never on the output document
    additionalFields: (c.additionalFields || []).map((f) => ({
      id: f.id,
      label: f.label,
      type: f.type,
      value: ctx.series.customFieldValues?.[f.id]?.value
        || ctx.series.wildlandData?.[f.id]
        || event.customFieldValues?.[f.id]?.value
        || '',
    })),
  };
}

function resolveContactBlock(config: DocumentConfig, ctx: MergeFieldContext): ResolvedContactBlock {
  const cb = config.contactBlock || {};
  return {
    introText: cb.introText ? resolveAllMergeFields(cb.introText, ctx) : undefined,
    phone: cb.showPhone ? resolveMergeField('departmentPhone', ctx) : undefined,
    email: cb.showEmail ? resolveMergeField('departmentEmail', ctx) : undefined,
    fax: cb.showFax ? resolveMergeField('departmentFax', ctx) : undefined,
  };
}

function resolveLegalText(config: DocumentConfig, ctx: MergeFieldContext): ResolvedLegalText {
  const lt = config.legalText || {};
  return {
    content: lt.content ? resolveAllMergeFields(lt.content, ctx) : '',
    position: lt.position || 'inline',
  };
}

function resolveFooter(config: DocumentConfig, ctx: MergeFieldContext): ResolvedFooter {
  const fc = config.footerConfig || {};
  return {
    departmentName: fc.showDepartmentInfo ? resolveMergeField('departmentName', ctx) : undefined,
    departmentPhone: fc.showDepartmentInfo ? resolveMergeField('departmentPhone', ctx) : undefined,
    showPageNumbers: fc.showPageNumbers !== false,
    customText: fc.customText ? resolveAllMergeFields(fc.customText, ctx) : undefined,
  };
}

function resolveStaticContent(
  config: DocumentConfig,
  sanitizeHtml: (html: string) => string,
): ResolvedStaticContent | undefined {
  const sc = config.staticContent;
  if (!sc) return undefined;
  return {
    type: sc.type || 'uploadedFile',
    label: sc.label,
    uploadedFileId: sc.uploadedFileId,
    // Static HTML content is rendered raw; sanitize via the injected sanitizer.
    htmlContent: sc.htmlContent ? sanitizeHtml(sc.htmlContent) : sc.htmlContent,
  };
}

// ---------------------------------------------------------------------------
// Violation grouping + photo selection (config-driven, identical for both callers)
// ---------------------------------------------------------------------------

/** Label shown for violations that have no parent question in the question-grouped layout. */
export const ADDITIONAL_VIOLATIONS_LABEL = 'Additional Violations';
/** Stable group key for the trailing additional-violations bucket. */
export const ADDITIONAL_VIOLATIONS_KEY = '__additional__';

/**
 * @process groupViolationsByQuestion
 * Steps:    Bucket violations under the inspection question that produced them (G-VIOL-QGROUP).
 *           A violation belongs to a question when it has a `questionId`/`questionText` and is not
 *           explicitly `source:'additional'`; otherwise it goes to the trailing Additional
 *           Violations bucket. Question groups keep first-seen order; the additional bucket is last.
 * Inputs:   items — already-mapped ResolvedViolationItem[] (each adapter populates questionId/text/source).
 * Outputs:  ResolvedViolationGroup[] (question groups, then at most one isAdditional bucket).
 * Failure:  Tolerant — items missing question linkage simply fall through to the additional bucket.
 * Idempotency: REQUIRED — pure, deterministic for a given input order.
 */
export function groupViolationsByQuestion(
  items: ResolvedViolationItem[],
): ResolvedViolationGroup[] {
  const questionGroups: ResolvedViolationGroup[] = [];
  const byKey = new Map<string, ResolvedViolationGroup>();
  const additional: ResolvedViolationItem[] = [];

  for (const item of items) {
    const key = item.questionId || item.questionText;
    const isQuestion = item.source !== 'additional' && !!key;
    if (!isQuestion) {
      additional.push(item);
      continue;
    }
    const mapKey = key as string;
    let group = byKey.get(mapKey);
    if (!group) {
      group = { groupLabel: item.questionText || item.questionId || 'Question', groupKey: mapKey, items: [] };
      byKey.set(mapKey, group);
      questionGroups.push(group);
    }
    group.items.push(item);
  }

  if (additional.length > 0) {
    questionGroups.push({
      groupLabel: ADDITIONAL_VIOLATIONS_LABEL,
      groupKey: ADDITIONAL_VIOLATIONS_KEY,
      items: additional,
      isAdditional: true,
    });
  }

  return questionGroups;
}

/**
 * Group already-mapped violation items by the document config's `groupBy`
 * (none/zone/category/severity/question). Each adapter maps its own raw violation shape (Mongo
 * canonical vs Dexie loose) into ResolvedViolationItem[] first; this grouping is then shared so
 * both surfaces label and order groups identically.
 */
export function groupViolations(
  items: ResolvedViolationItem[],
  config: DocumentConfig,
): { items: ResolvedViolationItem[]; groups: ResolvedViolationGroup[] } {
  const vc = config.violationConfig || { displayFormat: 'checklist' as const };
  const groupBy = vc.groupBy || 'none';
  const groups: ResolvedViolationGroup[] = [];

  if (groupBy === 'question') {
    return { items, groups: groupViolationsByQuestion(items) };
  }

  if (groupBy === 'none') {
    groups.push({ groupLabel: 'All Violations', groupKey: 'all', items });
  } else {
    const grouped = new Map<string, ResolvedViolationItem[]>();
    for (const item of items) {
      // `category` has no dedicated field on ResolvedViolationItem, so it falls back to the
      // violation code's leading character as a coarse code-family key (e.g. "101"/"105" → "1").
      // Items with no code land in the "Other" bucket. A precise category would require adapters
      // to populate a real `category` on the resolved item; until then this is the documented proxy.
      const key = groupBy === 'zone' ? (item.zone || 'Unassigned')
        : groupBy === 'category' ? (item.code?.charAt(0) || 'Other')
        : groupBy === 'severity' ? (item.severity || 'Unrated')
        : 'all';
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(item);
    }
    for (const [key, groupItems] of grouped) {
      groups.push({
        // Prefix zone/category keys so the heading reads as a labelled group rather than a bare
        // token (e.g. "Category 1" instead of "1"); "Other" and severity keys stand alone.
        groupLabel: groupBy === 'zone' ? `Zone ${key}`
          : groupBy === 'category' && key !== 'Other' ? `Category ${key}`
          : key,
        groupKey: key,
        items: groupItems,
      });
    }
    // Sort zone AND category groups by key (numeric-aware) so headings read in a stable, ascending
    // order regardless of the order items arrived in; "Other" sorts last (letters after digits).
    // Severity/none keep insertion order (severity has its own canonical ordering upstream).
    if (groupBy === 'zone' || groupBy === 'category') {
      groups.sort((a, b) => a.groupKey.localeCompare(b.groupKey, undefined, { numeric: true }));
    }
  }

  return { items, groups };
}

/** Slice already-mapped photos to the config's `maxPhotos` (all when unset). */
function selectPhotos(photos: ResolvedPhoto[], config: DocumentConfig): ResolvedPhoto[] {
  const max = config.photoConfig?.maxPhotos ?? photos.length;
  return photos.slice(0, max);
}

/**
 * @process distributeInlinePhotos
 * Steps:    1. Index photos by their `occurrenceId` (only occurrence-scoped photos participate).
 *           2. For each violation item, attach the matching photos to each occurrence
 *              (`occurrence.photos`) and collect the union onto `item.photos`.
 *           3. Return the residual photos (those not placed) for the standalone gallery so
 *              general/non-violation photos still render and no photo is dropped.
 * Inputs:   items — the (already-grouped) resolved violation items (mutated in place: freshly
 *           mapped per assemble call, so this stays idempotent w.r.t. identical inputs);
 *           photos — the full mapped photo set.
 * Outputs:  residual ResolvedPhoto[] to render in the gallery section.
 * Failure:  none — pure/total; with no occurrence linkage every photo is residual (gallery).
 * Idempotency: REQUIRED — same inputs yield the same attachment + residual set.
 *
 * DFE-005: this is the seam that makes the "inline with violations" layout actually place each
 * photo beneath its violation instead of dumping every photo into a flat gallery.
 */
function distributeInlinePhotos(
  items: ResolvedViolationItem[],
  photos: ResolvedPhoto[],
): ResolvedPhoto[] {
  // DFE-005: use String() coercion on all IDs to handle potential ObjectId instances or
  // mixed types from different data sources (MongoDB subdocument _id may be ObjectId at runtime).
  const byOccurrence = new Map<string, ResolvedPhoto[]>();
  // DFE-005 fallback: photos that have violationId but no occurrenceId (or whose occurrenceId
  // doesn't match any occurrence) are placed on the first occurrence of the matching violation.
  // This handles legacy data where occurrence._id was not set or got lost during sync/merge.
  const byViolation = new Map<string, ResolvedPhoto[]>();
  for (const photo of photos) {
    if (photo.occurrenceId) {
      const key = String(photo.occurrenceId);
      const existing = byOccurrence.get(key);
      if (existing) existing.push(photo);
      else byOccurrence.set(key, [photo]);
    } else if (photo.violationId) {
      const key = String(photo.violationId);
      const existing = byViolation.get(key);
      if (existing) existing.push(photo);
      else byViolation.set(key, [photo]);
    }
  }
  if (byOccurrence.size === 0 && byViolation.size === 0) return photos;

  const placed = new Set<ResolvedPhoto>();
  for (const item of items) {
    const itemPhotos: ResolvedPhoto[] = [];
    for (const occurrence of item.occurrences ?? []) {
      if (!occurrence.id) continue;
      const occurrencePhotos = byOccurrence.get(String(occurrence.id));
      if (!occurrencePhotos || occurrencePhotos.length === 0) continue;
      occurrence.photos = occurrencePhotos;
      for (const photo of occurrencePhotos) {
        itemPhotos.push(photo);
        placed.add(photo);
      }
    }
    // DFE-005 violationId fallback: if photos have violationId but no occurrenceId,
    // they land in byViolation and are matched to the item via _violationId.
    if (item._violationId) {
      const fallbackPhotos = byViolation.get(String(item._violationId));
      if (fallbackPhotos && fallbackPhotos.length > 0) {
        for (const photo of fallbackPhotos) {
          itemPhotos.push(photo);
          placed.add(photo);
        }
      }
    }
    if (itemPhotos.length > 0) item.photos = itemPhotos;
  }

  // DFE-005 second-pass fallback: photos that have occurrenceId but didn't match any occurrence
  // (e.g. the occurrence._id was undefined or mismatched). Try matching them by violationId
  // against the items' _violationId. This catches the common case where occurrenceId is set but
  // the violation item's occurrences[] have undefined/empty ids.
  const unplaced = photos.filter((photo) => !placed.has(photo) && photo.violationId);
  if (unplaced.length > 0) {
    // Build a violationId → item lookup
    const violationIdToItem = new Map<string, ResolvedViolationItem>();
    for (const item of items) {
      if (item._violationId) violationIdToItem.set(item._violationId, item);
    }
    for (const photo of unplaced) {
      if (!photo.violationId) continue;
      const matchingItem = violationIdToItem.get(String(photo.violationId));
      if (matchingItem) {
        if (!matchingItem.photos) matchingItem.photos = [];
        matchingItem.photos.push(photo);
        placed.add(photo);
      }
    }
  }

  return photos.filter((photo) => !placed.has(photo));
}

// ---------------------------------------------------------------------------
// Enabled-section gating (Finding 1 — the dual enable-flag seam fix)
// ---------------------------------------------------------------------------

/**
 * Maps a layout band kind to the DocumentConfig section id whose content it renders. Bands also
 * carry an `id` that equals the section id for converted templates (documentConfigToLayout), so
 * computeEnabledSections keys on both — this covers both converted and palette-built layouts.
 */
const BAND_KIND_TO_SECTION: Partial<Record<BandKind, string>> = {
  letterhead: 'header',
  coverPage: 'cover',
  recipientBlock: 'recipientBlock',
  subjectLine: 'subjectLine',
  salutation: 'salutation',
  metadataGrid: 'metadata',
  violationsList: 'violations',
  photoGallery: 'photos',
  signatureBlock: 'signatures',
  legal: 'legal',
  footer: 'footer',
  formResponses: 'formResponses',
};

/**
 * @process computeEnabledSections
 * Steps:    When a DocumentLayout is present, build the enabled-section set from the layout's
 *           enabled bands (header, footer, and each enabled body band → its section id + kind-mapped
 *           id). Otherwise fall back to config.sections[].enabled.
 * Inputs:   config (DocumentConfig), layout? (DocumentLayout — the canvas band model)
 * Outputs:  Set<string> of section ids whose content should be resolved/rendered.
 * Failure:  Tolerant — missing layout/body falls back to the section model; never throws.
 * Idempotency: REQUIRED — pure; same inputs always produce the same set.
 *
 * Why this exists: the canvas activation list flips a band's `enabled`, but content resolution
 * historically keyed off config.sections[].enabled (which the activation list never updated), so
 * checking a default-disabled element produced an empty band in both preview and PDF. Making the
 * layout authoritative when present closes that seam in one place for both callers.
 */
export function computeEnabledSections(config: DocumentConfig, layout?: DocumentLayout): Set<string> {
  if (layout && Array.isArray(layout.body)) {
    const enabled = new Set<string>();
    const addBand = (band: Band | null | undefined) => {
      if (!band || band.enabled === false) return;
      enabled.add(band.id);
      const mapped = BAND_KIND_TO_SECTION[band.kind];
      if (mapped) enabled.add(mapped);
    };
    addBand(layout.header);
    addBand(layout.footer);
    for (const band of layout.body) addBand(band);
    return enabled;
  }
  return new Set((config.sections || []).filter((s) => s.enabled).map((s) => s.id));
}

// ---------------------------------------------------------------------------
// Render-data assembly
// ---------------------------------------------------------------------------

/**
 * @contract AssembleDocumentDataInput
 * Purpose: Inputs to assembleDocumentRenderData. Each adapter normalizes its own source into these
 *          shared shapes, having already done the source-specific extraction that genuinely differs.
 * Fields:  config — the document template config; mergeContext — normalized merge inputs;
 *          violationItems — adapter-mapped (ungrouped) violations; photos — adapter-mapped (unsliced);
 *          signatures — adapter-resolved signatures; formResponses? — worker-only resolved form data;
 *          documentLayout? — the canvas band model (drives Finding-1 gating when present, and the
 *          letterhead logo-slot override — see G-LOGO-05 below); branding? — department brand
 *          colors (worker + preview both pass these now, Finding 2); resolveUploadedFileUrl? —
 *          maps a template-specific uploaded logo's UploadedFile id to a displayable URL (blob/SAS);
 *          advancedFields? — adapter-resolved advanced-field bindings; advancedBinding? — section
 *          render toggle + label; sanitizeHtml? — injected HTML sanitizer (identity when omitted).
 * Rules:   violationItems/photos are passed in full; this function applies config-driven grouping
 *          and slicing. Gating is decided here so the two callers cannot diverge again.
 * Sync:    pure value object; no I/O.
 */
export interface AssembleDocumentDataInput {
  config: DocumentConfig;
  mergeContext: MergeFieldContext;
  violationItems: ResolvedViolationItem[];
  photos: ResolvedPhoto[];
  signatures: ResolvedSignatures;
  formResponses?: ResolvedFormResponseGroup[];
  documentLayout?: DocumentLayout;
  branding?: { primary: string; secondary?: string };
  /**
   * Resolves a template-specific uploaded logo's UploadedFile id to a displayable URL (blob/SAS).
   * Only consulted for letterhead band logo slots whose source is 'uploaded' (G-LOGO-05); department-
   * sourced slots resolve from mergeContext.department.branding instead. Omit when the caller has no
   * canvas-uploaded logos to resolve (e.g. no documentLayout, or department-only logo slots).
   */
  resolveUploadedFileUrl?: (uploadedFileId: string) => string | undefined;
  advancedFields?: ResolvedAdvancedField[];
  advancedBinding?: { renderAdvancedSection?: boolean; advancedSectionLabel?: string };
  sanitizeHtml?: (html: string) => string;
}

/**
 * @process assembleDocumentRenderData
 * Steps:    1. Decide enabled sections (layout-aware via computeEnabledSections).
 *           2. Resolve each enabled text section + group violations + slice photos.
 *           3. Attach branding, reportNumber, generatedAt, currentYear.
 *           4. G-LOGO-05: when a documentLayout with a letterhead band is present, override the
 *              resolved header's logo URLs via applyLayoutHeaderLogos, using the department's RAW
 *              (ungated) logo URLs — not the legacy showLogo/showSecondLogo-gated values from step 2
 *              — so a band slot's own `show`/`departmentLogoSlot` fully controls what renders,
 *              independent of the legacy per-document flags a canvas template can no longer edit.
 *           5. Attach adapter-resolved advanced fields and substitute {{advField:id}} tokens in
 *              HTML body paragraphs; set the advanced-section render toggle + label.
 * Inputs:   AssembleDocumentDataInput (see contract).
 * Outputs:  DocumentRenderData consumed identically by the worker PDF renderer and the web preview.
 * Failure:  Tolerant of missing config sub-objects; resolvers never throw on absent source data.
 * Idempotency: REQUIRED — pure; the same inputs always produce the same render data.
 */
export function assembleDocumentRenderData(input: AssembleDocumentDataInput): DocumentRenderData {
  const {
    config,
    mergeContext: ctx,
    violationItems,
    photos,
    signatures,
    formResponses,
    documentLayout,
    branding,
    resolveUploadedFileUrl,
    advancedFields,
    advancedBinding,
  } = input;
  const sanitizeHtml = input.sanitizeHtml ?? identitySanitize;

  const enabled = computeEnabledSections(config, documentLayout);
  const hasViolations = ctx.violations.length > 0;

  const grouped = enabled.has('violations')
    ? groupViolations(violationItems, config)
    : { items: [], groups: [] };

  // DFE-005: when the photo layout is "inline with violations", place each occurrence-scoped photo
  // beneath its violation (attached onto grouped.items / their occurrences) and keep only the
  // residual (general/unmatched) photos in the standalone gallery. Table format has no per-violation
  // render slot, so it falls back to the gallery to avoid dropping photos.
  const photosEnabled = enabled.has('photos');
  const displayFormat = config.violationConfig?.displayFormat || 'checklist';
  const useInlinePhotos =
    photosEnabled && config.photoConfig?.layout === 'inline' && displayFormat !== 'table';
  const galleryPhotos = useInlinePhotos
    ? distributeInlinePhotos(grouped.items, photos)
    : photos;

  const renderData: DocumentRenderData = {
    documentConfig: config,

    header: enabled.has('header') ? resolveHeader(config, ctx) : undefined,
    recipientBlock: enabled.has('recipientBlock') ? resolveRecipientBlock(config, ctx) : undefined,
    subjectLine: enabled.has('subjectLine') ? resolveSubjectLine(config, ctx) : undefined,
    salutation: enabled.has('salutation') ? resolveSalutation(config, ctx) : undefined,
    bodyParagraphs: enabled.has('bodyText') ? resolveBodyText(config, ctx, hasViolations, sanitizeHtml) : undefined,
    metadataFields: enabled.has('metadata') ? resolveMetadata(config, ctx) : undefined,

    violations: enabled.has('violations') ? grouped.items : undefined,
    violationGroups: enabled.has('violations') ? grouped.groups : undefined,
    hasViolations,

    photos: photosEnabled ? selectPhotos(galleryPhotos, config) : undefined,
    comments: enabled.has('comments') ? resolveComments(config, ctx) : undefined,
    contactBlock: enabled.has('contactBlock') ? resolveContactBlock(config, ctx) : undefined,
    signatures: enabled.has('signatures') ? signatures : undefined,
    legalText: enabled.has('legal') ? resolveLegalText(config, ctx) : undefined,
    footer: enabled.has('footer') ? resolveFooter(config, ctx) : undefined,
    staticContent: enabled.has('staticContent') ? resolveStaticContent(config, sanitizeHtml) : undefined,
    formResponses: enabled.has('formResponses') ? formResponses : undefined,

    reportNumber: ctx.reportNumber,
    generatedAt: formatDateShort(ctx.generatedAt),
    // Derive from the input stamp (not new Date()) so the assembler is genuinely pure — same inputs
    // always produce the same render data, matching the @process Idempotency contract above.
    currentYear: ctx.generatedAt.getFullYear(),
    // Brand colors — injected as CSS custom properties by the shared renderer. Both the worker and
    // the preview pass these now so a department-default ('none') template renders the same colors
    // in preview and PDF (Finding 2).
    branding: branding ? { primary: branding.primary, secondary: branding.secondary } : undefined,
  };

  // G-LOGO-05: the canvas letterhead band's logo slots are authoritative when a documentLayout is
  // present — override the section-2-resolved header logo URLs with the band-driven resolution,
  // sourced from the department's RAW (ungated) branding so a `department`-sourced secondary slot
  // renders even though the legacy showSecondLogo document flag defaults false and can no longer be
  // edited once the canvas takes over. This also folds in template-specific `uploaded` band logos
  // (single source of truth for the worker PDF and both web previews — no per-caller duplication).
  if (documentLayout && renderData.header) {
    renderData.header = applyLayoutHeaderLogos(documentLayout, renderData, {
      departmentLogoUrl: resolveDepartmentLogoUrl(ctx.department.branding),
      departmentSecondLogoUrl: resolveDepartmentSecondLogoUrl(ctx.department.branding),
      resolveUploadedFileUrl,
    }).header;
  }

  if (advancedFields && advancedFields.length > 0) {
    // Sanitize each resolved advanced-field value once, at the single assembly point, so EVERY
    // consumer of `renderData.advancedFields` inherits the same sanitizer the bodyParagraph path
    // uses. The static-band token-substitution path (renderSections.renderBandToHtml) has no
    // sanitizer of its own and substitutes these values into PDF HTML directly; sanitizing at the
    // source makes that path safe-by-construction instead of relying on the caller having
    // pre-sanitized the layout. Checkbox glyphs (☑/☐) pass through unchanged; only free-text
    // values carrying markup are cleaned (G-SECURITY parity with the bodyParagraph path).
    const sanitizedAdvancedFields = advancedFields.map((f) => ({ ...f, value: sanitizeHtml(f.value) }));
    renderData.advancedFields = sanitizedAdvancedFields;
    if (renderData.bodyParagraphs) {
      renderData.bodyParagraphs = renderData.bodyParagraphs.map((p) =>
        p.isHtml
          ? { ...p, content: sanitizeHtml(resolveAdvancedTokensInHtml(p.content, sanitizedAdvancedFields)) }
          : p,
      );
    }
  }

  renderData.renderAdvancedSection = advancedBinding?.renderAdvancedSection !== false;
  renderData.advancedSectionLabel = advancedBinding?.advancedSectionLabel || 'Inspection Data';

  return renderData;
}
